import { Link, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import ChipLabel from '/@/components/chip-label';
import useUnitProvider from '/@/core/context/unit';
import { useNextVisitDashboard } from '/@/hooks/dashboards';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { parseUserNode, useGetUserNodes, useInvitationByUser, useSendFieldviewInvitation } from '/@/hooks/nodes';
import {
    useHasWriteAccess,
    useIsValidatedAdvisor,
    useIsValidatedConsultant,
    useIsValidatedRV,
    useIsValidatedSquad,
} from '/@/hooks/session';
import { useVisitReports, type VisitReport } from '/@/hooks/visit-reports';
import { _ } from '/@/locale';
import { getEnableRvAdvisorOnboarding, getUser } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Grower } from '/@/types/growers';
import { reverse } from '/@/utils/router';
import NextVisitModal from '/@/views/dashboard/grower/components/next-visit-modal';
import { getCrops } from '/@/views/dashboard/hooks/crops';

import { VisitReportRow } from '/@/views/fields/field-monitoring/components/visit-reports-list';
import GrowersListActions from '../../../../growers-list-actions';
import AssignAdvisorModal from '../../../components/assign-advisor-modal';
import CropDistributionBar, { type CropDistributionItem } from '../../../components/crop-distribution-bar';
import GrowerNameCell from '../../../components/grower-name-cell';

const CampaignGrowerRow = ({
    grower,
    cropFilter,
    setNewFieldGrowersModalOpen,
    setNewFieldModalGrowerId,
    onGrowerDeleteSuccess,
}: {
    grower: Grower;
    cropFilter: string;
    setNewFieldGrowersModalOpen?: (open: boolean) => void;
    setNewFieldModalGrowerId?: (growerId: number | undefined) => void;
    onGrowerDeleteSuccess: (growerName: string) => void;
}) => {
    const navigate = useNavigate();
    const units = useUnitProvider();
    const user = useSelector(getUser);
    const isAdvisor = useIsValidatedAdvisor();

    const enableRvAdvisorOnboarding = useSelector(getEnableRvAdvisorOnboarding);

    // Transform API data to component format
    const transformGrowerData = useCallback(
        (grower: Grower) => {
            const parsedUserNode = parseUserNode(grower, user);
            const fields = grower.fields || {};
            const countByCrop = fields.countByCrop || {};
            const totalFields = fields.count || 0;

            // Calculate crop distribution
            const crops = getCrops();
            const cropDistribution: CropDistributionItem[] = [];
            if (totalFields > 0) {
                Object.entries(countByCrop).forEach(([crop, fields]) => {
                    const cropData = crops[crop as keyof typeof crops];
                    if (cropData && Number(fields) > 0) {
                        cropDistribution.push({
                            crop,
                            getChip: cropData?.getChip,
                            percentage: Math.round((Number(fields) / totalFields) * 100),
                            fields: Number(fields),
                            icon: cropData?.icon,
                            color: cropData?.color,
                            label: cropData?.name,
                        });
                    }
                });

                // Sort by order from getCrops
                cropDistribution.sort((a, b) => {
                    const cropA = crops[a.crop as keyof typeof crops];
                    const cropB = crops[b.crop as keyof typeof crops];
                    return (cropA?.order || 999) - (cropB?.order || 999);
                });
            }

            // Generate suggestions

            const fieldviewConnected = grower.user?.authProviders?.includes('fieldview');

            return {
                id: grower.id,
                name: parsedUserNode.isSelf ? _('Mi cuenta') : parsedUserNode.name,
                location: parsedUserNode.location,
                totalFields,
                totalArea: convertAreaFromM2(grower?.fields.totalArea || 0, units).toString(),
                fieldviewConnected: fieldviewConnected,
                hasAssignedUser: grower.user !== null,
                cropDistribution,
                phone: parsedUserNode.phone,
                email: parsedUserNode.email,
                isSelf: parsedUserNode.isSelf,
                subtype: parsedUserNode.subtype,
            };
        },
        [units, user]
    );

    const isConsultant = useIsValidatedConsultant();
    const isRv = useIsValidatedRV();
    const hasWriteAccess = useHasWriteAccess();
    const isValidatedSquad = useIsValidatedSquad();

    const transformedGrower = useMemo(() => transformGrowerData(grower), [grower, transformGrowerData]);
    const nextVisitDashboardQuery = useNextVisitDashboard({ id: grower.id });
    const [openNextVisitModal, setOpenNextVisitModal] = useState(false);

    const visitReportsQuery = useVisitReports(
        {
            user_node: grower.id,
            page: 1,
            page_size: 2,
            status: !hasWriteAccess ? 'saved' : undefined,
        },
        { enabled: isValidatedSquad }
    );

    const { invitationStatus, invitationSendedMoreThan7DaysAgo } = useInvitationByUser({
        userNodeId: grower.id,
    });

    const sendFieldviewInvitationMutation = useSendFieldviewInvitation();

    const parentNodesQuery = useGetUserNodes(grower.parents || []);
    const advisorParent = useMemo(
        () => parentNodesQuery.data?.find((n) => n?.type === 'advisor'),
        [parentNodesQuery.data]
    );

    const [openAssignAdvisorModal, setOpenAssignAdvisorModal] = useState(false);

    const [selectedAdvisorId, setSelectedAdvisorId] = useState('');

    return (
        <>
            <TableRow
                key={transformedGrower.id}
                sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
                onClick={() => {
                    navigate(
                        reverse('Growers.detail', {
                            params: {
                                growerNodeId: transformedGrower.id,
                            },
                            qs: {
                                tab: 'current-campaign',
                            },
                        })
                    );
                }}
            >
                <TableCell width='20%'>
                    <GrowerNameCell
                        name={transformedGrower.name}
                        location={transformedGrower.location}
                        hasAssignedUser={transformedGrower.hasAssignedUser}
                        invitationStatus={invitationStatus}
                    />
                </TableCell>
                {isAdvisor ? (
                    <TableCell width='15%'>
                        {transformedGrower.subtype === 'common' ? (
                            <ChipLabel type='commonGrower' label={_('Otros segmentos')} />
                        ) : (
                            <ChipLabel type='premiumGrower' label={_('Premium')} />
                        )}
                    </TableCell>
                ) : enableRvAdvisorOnboarding ? (
                    isRv ? (
                        <TableCell width='15%'>
                            {advisorParent ? (
                                <ChipLabel type='advisor' label={advisorParent.name} />
                            ) : (
                                <Link
                                    component='button'
                                    type='button'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenAssignAdvisorModal(true);
                                    }}
                                >
                                    {_('Asignar Asesor')}
                                </Link>
                            )}
                        </TableCell>
                    ) : (
                        <TableCell width='15%'>
                            <Tooltip title={_('Solo los RVs pueden asignar asesor a los productores')} arrow>
                                <div>
                                    <TableTemplateCell title={_('No asignado')} />
                                </div>
                            </Tooltip>
                        </TableCell>
                    )
                ) : null}
                <TableCell width='20%'>
                    {!['pending', 'expired'].includes(invitationStatus || '') ? (
                        transformedGrower.totalFields === 0 ? (
                            <TableTemplateCell
                                subtitleButton={hasWriteAccess && _('Agregar Lote')}
                                subtitleButtonClick={(e) => {
                                    e.stopPropagation();
                                    setNewFieldModalGrowerId?.(transformedGrower.id);
                                    setNewFieldGrowersModalOpen?.(true);
                                }}
                            />
                        ) : (
                            <TableTemplateCell
                                title={_('{{count}} Lote', { count: transformedGrower.totalFields })}
                                subtitle={_('Superficie total: {{value}}', { value: transformedGrower.totalArea })}
                            />
                        )
                    ) : (
                        <TableTemplateCell
                            title={
                                invitationStatus === 'pending'
                                    ? _('Acceso a FieldView pendiente')
                                    : _('Acceso a FieldView expirado')
                            }
                            subtitleButton={hasWriteAccess && _('Reenviar solicitud')}
                            subtitleButtonClick={(e) => {
                                e.stopPropagation();
                                sendFieldviewInvitationMutation.trigger({
                                    invitedUserName: transformedGrower.name,
                                    invitedUserEmail: transformedGrower.email,
                                    sharedNodeId: transformedGrower.id,
                                });
                            }}
                            subtitleButtonDisabled={
                                !invitationSendedMoreThan7DaysAgo || sendFieldviewInvitationMutation.isMutating
                            }
                            subtitleButtonTooltip={
                                invitationSendedMoreThan7DaysAgo ? '' : _('La solicitud se envio hace menos de 7 días')
                            }
                        />
                    )}
                </TableCell>
                <TableCell width='30%'>
                    {transformedGrower.cropDistribution.length > 0 ? (
                        <CropDistributionBar
                            distribution={transformedGrower.cropDistribution}
                            cropFilter={cropFilter}
                        />
                    ) : null}
                </TableCell>
                <TableCell width='30%'>
                    {isValidatedSquad ? (
                        <TableTemplateCell
                            title={_('{{count}} informes', { count: visitReportsQuery.data?.count || 0 })}
                            subtitle={
                                <Stack direction='row' spacing={1} flexWrap='nowrap'>
                                    {visitReportsQuery.data?.results.map((visitReport) => (
                                        <VisitReportRow
                                            key={visitReport.id}
                                            visitReport={visitReport as VisitReport}
                                            simplifiedVersion
                                        />
                                    ))}
                                </Stack>
                            }
                        />
                    ) : (
                        nextVisitDashboardQuery.data && (
                            <TableTemplateCell
                                title={
                                    <Typography variant='subtitle2' color='primary'>
                                        {_(nextVisitDashboardQuery.data.description)}
                                    </Typography>
                                }
                                subtitleButton={(isRv || isConsultant) && _('Coordinar visita')}
                                subtitleButtonClick={(e) => {
                                    e.stopPropagation();
                                    mpTrackEvent('Visit Scheduling flow:Started', {
                                        grower_id: transformedGrower.id,
                                    });
                                    setOpenNextVisitModal(true);
                                }}
                            />
                        )
                    )}
                </TableCell>
                <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                    <GrowersListActions
                        growerNodeId={transformedGrower.id}
                        editable={!transformedGrower.isSelf}
                        growerPhone={transformedGrower.phone || ''}
                        growerName={transformedGrower.name}
                        tab='current-campaign'
                        onGrowerDeleteSuccess={onGrowerDeleteSuccess}
                        deletable={!transformedGrower.hasAssignedUser}
                    />
                </TableCell>
            </TableRow>
            {openNextVisitModal && (
                <NextVisitModal
                    open={openNextVisitModal}
                    onClose={(e) => {
                        e.stopPropagation();
                        setOpenNextVisitModal(false);
                    }}
                    growerName={transformedGrower.name}
                    growerPhone={transformedGrower.phone}
                />
            )}

            <AssignAdvisorModal
                open={openAssignAdvisorModal}
                setOpen={setOpenAssignAdvisorModal}
                grower={grower}
                selectedAdvisorId={selectedAdvisorId}
                setSelectedAdvisorId={setSelectedAdvisorId}
            />
        </>
    );
};

export default CampaignGrowerRow;
